import { useEffect, useState, useRef } from "react";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import MobileFrame from "../../components/MobileFrame";

import {
    ArrowRight,
    Lock,
    CheckCircle2,
    CalendarClock,
    ShoppingBag,
    ChevronLeft,
} from "lucide-react";

import successAnimation from "../../assets/Success.json";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "../../store";
import { Loader2 } from "lucide-react";
import { initiateDownPayment } from "../../api/downpayment.api";
import { updateJourneyPhase } from "../../api/journey.api";
import { setJourney } from "../../store/journeySlice";

interface LoanSummaryDetails {
    orderAmount: string;
    loanAmount: string;
    downPayment: string;
    emiPerMonth: string;
    processingFee: string;
    interestRate: string;
    productDetails: string;
    orderId: string;
}

interface DashboardProps {
    onStartStep: (stepKey: string) => void;
    onViewLoanSummary?: () => void;
    loanSummary?: LoanSummaryDetails;
    redirectUrl?: string;
    redirectSeconds?: number;
}



const STEP_UI = {
    KYC: {
        title: "Complete KYC",
        detail: "Verify your identity in just 2 minutes",
        cta: "Start KYC",
    },

    MANDATE: {
        title: "Mandate/AutoPay Setup",
        detail: "Link your bank account for quick and hassle-free auto-pay",
        cta: "Setup Mandate",
    },

    AGREEMENT: {
        title: "Agreement Signing",
        detail: "Complete it in under a minute : email OTP and e-sign",
        cta: "Sign Agreement",
    },

    DOWNPAYMENT: {
        title: "Complete Downpayment",
        detail: "Final step : pay via UPI or Card",
        cta: "Pay Downpayment",
    },
} as const;

const Dashboard = ({
    onStartStep,
    onViewLoanSummary,
    redirectUrl = "https://cleverpe.com",
    redirectSeconds = 10,
}: DashboardProps) => {
    /*
     * ============================================================
     * REDUX JOURNEY STATE
     * ============================================================
     */

    const navigate = useNavigate();

    const dispatch = useDispatch<AppDispatch>();

    const journey = useSelector(
        (state: RootState) => state.journey.journey
    );

    /*
     * Customer information comes from backend journey.
     */
    const customerName =
        journey?.customerName?.trim().split(/\s+/)[0] ?? "";

    const preApprovedAmount =
        journey?.approvalLimit ?? 0;

    /*
     * Backend-driven journey state.
     */
    const completedSteps =
        journey?.completedSteps ?? [];

    const stepSequence =
        journey?.stepSequence ?? [];

    const currentStep =
        journey?.currentStep ?? null;

    const steps =
        journey?.steps ?? {};


    const totalSteps =
        stepSequence.length;

    const completedCount =
        completedSteps.length;

    const progressPercent =
        totalSteps > 0
            ? Math.round(
                (completedCount / totalSteps) * 100
            )
            : 0;

    const remainingSteps =
        Math.max(
            0,
            totalSteps - completedCount
        );

    const isAllCompleted =
        journey?.status === "COMPLETED";

    const firstEmiDueDate = new Date(
        new Date().setMonth(new Date().getMonth() + 1)
    ).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
    const successLottieRef = useRef<LottieRefCurrentProps>(null);



    /*
 * ============================================================
 * DOWNPAYMENT — PAY NOW
 * ============================================================
 *
 * Unlike other steps, DOWNPAYMENT does NOT navigate to a
 * separate page. The dashboard calls the payment-initiation
 * API directly and redirects straight to the payment link.
 */

    const [isProcessingPayment, setIsProcessingPayment] =
        useState(false);

    const [paymentError, setPaymentError] =
        useState<string | null>(null);

    /*
     * ============================================================
     * BACK — RETURN TO SCHEME SELECTION
     * ============================================================
     *
     * Only reachable when no step has been completed yet (see
     * render below). Mirrors SchemePage's handleBack: the forward
     * transition (scheme selection -> dashboard/KYC) updates the
     * journey phase on the backend, so going back must revert it
     * symmetrically to SCHEME_SELECTION. Otherwise a refresh or
     * re-entry after navigating back would still resolve to the
     * KYC phase and bounce the user forward again.
     */

    const [isGoingBack, setIsGoingBack] =
        useState(false);

    const handleBack = async () => {

        if (isGoingBack || isProcessingPayment) {
            return;
        }

        if (!journey) {

            navigate("/nscheme-selection", { replace: true });

            return;
        }

        try {

            setIsGoingBack(true);

            const response =
                await updateJourneyPhase("SCHEME_SELECTION");

            if (response?.data) {

                dispatch(setJourney(response.data));

            }

            navigate("/nscheme", { replace: true });

        } catch (error) {

            console.error(
                "Failed to revert journey phase:",
                error
            );

        } finally {

            setIsGoingBack(false);

        }

    };

    const handlePayDownpayment = async () => {

        if (
            isProcessingPayment ||
            !journey
        ) {
            return;
        }

        try {

            setPaymentError(null);

            setIsProcessingPayment(true);

            const response =
                await initiateDownPayment({
                    orderId: journey.orderId,
                    email: journey.customerEmail,
                    firstname: journey.customerName,
                    amount: journey.dpAmount,
                });

            if (
                !response.success ||
                !response.paymentLink
            ) {

                throw new Error(
                    "Payment link was not generated."
                );

            }

            window.location.href =
                response.paymentLink;

        } catch (error) {

            console.error(
                "Down payment initiation failed:",
                error
            );

            setPaymentError(
                error instanceof Error
                    ? error.message
                    : "Unable to initiate payment. Please try again."
            );

            setIsProcessingPayment(false);

        }

    };

    /*
     * ============================================================
     * REDIRECT STATE
     * ============================================================
     */

    const [secondsLeft, setSecondsLeft] =
        useState(redirectSeconds);

    useEffect(() => {
        if (!isAllCompleted) {
            return;
        }

        setSecondsLeft(
            redirectSeconds
        );

        const interval =
            setInterval(() => {
                setSecondsLeft((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval);

                        window.location.href =
                            redirectUrl;

                        return 0;
                    }

                    return prev - 1;
                });
            }, 1000);

        return () => {
            clearInterval(interval);
        };
    }, [
        isAllCompleted,
        redirectUrl,
        redirectSeconds,
    ]);

    /*
     * ============================================================
     * STEP ACTION
     * ============================================================
     *
     * Dashboard does NOT change the journey itself.
     *
     * It only tells LoanApplicationFlow:
     *
     * "User wants to start KYC"
     *
     * The actual API calls will update the journey.
     */

    const handleStartCurrentStep = (
        stepKey: string
    ) => {
        if (stepKey !== currentStep) {
            return;
        }

        /*
         * DOWNPAYMENT is handled entirely on this
         * page — see handlePayDownpayment.
         */
        if (stepKey === "DOWNPAYMENT") {
            return;
        }

        onStartStep(stepKey);
    };

    /*
     * ============================================================
     * REDIRECT PROGRESS
     * ============================================================
     */

    const redirectProgress =
        redirectSeconds > 0
            ? (
                (redirectSeconds -
                    secondsLeft) /
                redirectSeconds
            ) * 100
            : 0;

    /*
     * ============================================================
     * RENDER
     * ============================================================
     */

    return (
        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >
            <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                <div className="absolute w-64 h-64 bg-purple-200 rounded-full -top-24 -left-20 blur-3xl opacity-20" />

                <div className="absolute w-64 h-64 bg-blue-200 rounded-full -bottom-24 -right-20 blur-3xl opacity-20" />

                <div className="relative z-10 flex flex-col h-full px-6 pt-6 pb-4">

                    {/* =====================================================
                        HEADER
                    ===================================================== */}

                    <div className="flex items-center justify-between shrink-0">

                        {!isAllCompleted && (
                            <div className="flex items-center gap-3">

                                {completedCount === 0 && (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        disabled={
                                            isGoingBack ||
                                            isProcessingPayment
                                        }
                                        aria-label="Go back to scheme selection"
                                        className="
                                            flex h-9 w-9
                                            items-center justify-center
                                            rounded-full
                                            border border-slate-200
                                            bg-white
                                            text-gray-600
                                            shadow-sm
                                            active:scale-95
                                            transition-transform
                                            disabled:cursor-not-allowed
                                            disabled:opacity-50
                                        "
                                    >

                                        {isGoingBack ? (
                                            <Loader2
                                                className="h-4.5 w-4.5 animate-spin"
                                                aria-hidden="true"
                                            />
                                        ) : (
                                            <ChevronLeft
                                                className="h-4.5 w-4.5"
                                                aria-hidden="true"
                                            />
                                        )}

                                    </button>
                                )}

                                <p className="text-base font-semibold text-gray-900">
                                    Hello, {customerName}!
                                </p>

                            </div>
                        )}

                        <img
                            src="/CleverPeLogos/CleverPE_Black_icon.svg"
                            alt="CleverPe"
                            className={`h-6 w-6 opacity-90 ${isAllCompleted
                                ? "ml-auto"
                                : ""
                                }`}
                        />

                    </div>

                    {/* =====================================================
                        IN-PROGRESS JOURNEY
                    ===================================================== */}

                    {!isAllCompleted ? (
                        <>
                            {/* =================================================
                                PROGRESS CARD
                            ================================================= */}

                            <div className="relative mt-3 shrink-0">

                                <div className="relative overflow-hidden rounded-[22px] border border-slate-100 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.03),0_10px_20px_rgba(15,23,42,0.06),0_20px_40px_rgba(15,23,42,0.04)] transition-all duration-300">

                                    <div className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-white/70" />

                                    <div className="relative z-10 flex items-center gap-4">

                                        {/* Progress circle */}

                                        <div className="relative flex items-center justify-center shrink-0">

                                            {(() => {
                                                const radius = 30;
                                                const stroke = 6;

                                                const circumference =
                                                    2 *
                                                    Math.PI *
                                                    radius;

                                                const offset =
                                                    circumference -
                                                    (progressPercent /
                                                        100) *
                                                    circumference;

                                                return (
                                                    <svg
                                                        width="72"
                                                        height="72"
                                                        className="-rotate-90"
                                                    >
                                                        <circle
                                                            cx="36"
                                                            cy="36"
                                                            r={radius}
                                                            fill="none"
                                                            stroke="#EEF2FF"
                                                            strokeWidth={
                                                                stroke
                                                            }
                                                        />

                                                        <circle
                                                            cx="36"
                                                            cy="36"
                                                            r={radius}
                                                            fill="none"
                                                            stroke="url(#progressGradient)"
                                                            strokeWidth={
                                                                stroke
                                                            }
                                                            strokeLinecap="round"
                                                            strokeDasharray={
                                                                circumference
                                                            }
                                                            strokeDashoffset={
                                                                offset
                                                            }
                                                            style={{
                                                                transition:
                                                                    "stroke-dashoffset 0.8s ease",
                                                            }}
                                                        />

                                                        <defs>
                                                            <linearGradient
                                                                id="progressGradient"
                                                                x1="0%"
                                                                y1="0%"
                                                                x2="100%"
                                                                y2="0%"
                                                            >
                                                                <stop
                                                                    offset="0%"
                                                                    stopColor="#6366F1"
                                                                />

                                                                <stop
                                                                    offset="100%"
                                                                    stopColor="#60A5FA"
                                                                />
                                                            </linearGradient>
                                                        </defs>
                                                    </svg>
                                                );
                                            })()}

                                            <div className="absolute flex flex-col items-center">

                                                <span className="text-lg font-bold bg-gradient-to-r from-indigo-500 to-blue-400 bg-clip-text text-transparent">
                                                    {progressPercent}%
                                                </span>

                                            </div>

                                        </div>

                                        {/* Progress information */}

                                        <div className="flex-1 flex flex-col justify-between h-[82px] min-w-0">

                                            <div>

                                                <p className="text-[13px] font-semibold text-gray-900">
                                                    Your application
                                                </p>

                                                <p className="mt-0.5 text-base font-bold leading-5 bg-gradient-to-r from-indigo-500 to-blue-400 bg-clip-text text-transparent inline-block">
                                                    Just{" "}
                                                    {remainingSteps}{" "}
                                                    {remainingSteps === 1
                                                        ? "step"
                                                        : "steps"}{" "}
                                                    left to complete
                                                </p>

                                            </div>

                                            <div className="flex items-center justify-between gap-1.5 min-w-0">

                                                <span className="whitespace-nowrap shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 shadow-sm">

                                                    Approval Limit:{" "}

                                                    <span className="font-bold text-emerald-600">
                                                        ₹
                                                        {preApprovedAmount.toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </span>

                                                </span>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            </div>

                            {/* =================================================
                                LOAN JOURNEY
                            ================================================= */}

                            <div className="relative mt-4 flex-1 min-h-0 overflow-y-auto">

                                <div className="mb-5 flex items-center justify-between">

                                    <div>

                                        <h2 className="text-[15px] font-semibold text-gray-900">
                                            Loan Journey
                                        </h2>

                                        <p className="mt-0.5 text-xs text-gray-500">
                                            Complete each step to finish your application
                                        </p>

                                    </div>

                                    {completedCount > 1 ? (
                                        <button
                                            onClick={() =>
                                                onViewLoanSummary?.()
                                            }
                                            className="-mt-3.5 flex items-center gap-1 min-w-0 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-600 shadow-sm hover:bg-indigo-100 transition"
                                        >
                                            <span className="truncate">
                                                Loan Summary
                                            </span>

                                            <ArrowRight className="h-3 w-3 shrink-0" />
                                        </button>
                                    ) : (
                                        <span />
                                    )}

                                </div>

                                <div className="relative">

                                    <div className="absolute left-5 top-6 bottom-6 w-[2px] rounded-full bg-gradient-to-b from-indigo-500 to-blue-400" />

                                    <div className="space-y-5">

                                        {stepSequence.map(
                                            (
                                                stepKey,
                                                index
                                            ) => {

                                                const step =
                                                    steps[
                                                    stepKey
                                                    ];

                                                const ui =
                                                    STEP_UI[
                                                    stepKey as keyof typeof STEP_UI
                                                    ];

                                                /*
                                                 * Safety:
                                                 * If backend adds a new
                                                 * step for which UI metadata
                                                 * does not exist, don't crash.
                                                 */
                                                if (!ui) {
                                                    return null;
                                                }

                                                const isCompleted =
                                                    step?.status ===
                                                    "COMPLETED";

                                                const isCurrent =
                                                    stepKey ===
                                                    currentStep;

                                                const isLocked =
                                                    step?.status ===
                                                    "LOCKED";

                                                return (
                                                    <div
                                                        key={
                                                            stepKey
                                                        }
                                                        className="relative flex items-start gap-3"
                                                    >

                                                        {/* Step number */}

                                                        <div className="relative z-20 flex justify-center shrink-0 w-10">

                                                            {isCompleted ? (
                                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 border border-green-200 shadow-sm">

                                                                    <CheckCircle2 className="h-4.5 w-4.5 text-green-500" />

                                                                </div>
                                                            ) : isCurrent ? (
                                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 text-sm font-bold text-white shadow-lg ring-4 ring-indigo-50">

                                                                    {index +
                                                                        1}

                                                                </div>
                                                            ) : (
                                                                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-500">

                                                                    {index +
                                                                        1}

                                                                </div>
                                                            )}

                                                        </div>

                                                        {/* Current step */}

                                                        {isCurrent ? (
                                                            <div className="group relative flex-1 rounded-2xl border border-indigo-300 bg-white p-3.5 ring-1 ring-indigo-100/70 shadow-[0_4px_12px_rgba(15,23,42,0.06),0_10px_24px_rgba(15,23,42,0.05)] transition-all duration-300 hover:-translate-y-1 hover:border-indigo-400 hover:ring-indigo-200">

                                                                <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/70" />

                                                                <h3 className="text-[14px] font-semibold text-gray-900 leading-tight truncate">
                                                                    {ui.title}
                                                                </h3>

                                                                <p className="mt-2.5 text-[11.5px] text-gray-500 leading-snug">
                                                                    {ui.detail}
                                                                </p>

                                                                {stepKey === "DOWNPAYMENT" ? (
                                                                    <>
                                                                        <button
                                                                            onClick={handlePayDownpayment}
                                                                            disabled={isProcessingPayment}
                                                                            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 py-2.5 text-[13px] font-semibold text-white shadow-md transition-transform active:scale-[0.98] disabled:opacity-60"
                                                                        >
                                                                            {isProcessingPayment ? (
                                                                                <>
                                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                                    Processing Payment...
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    Pay ₹
                                                                                    {(
                                                                                        journey?.dpAmount ?? 0
                                                                                    ).toLocaleString("en-IN")}
                                                                                    <ArrowRight className="h-4 w-4" />
                                                                                </>
                                                                            )}
                                                                        </button>

                                                                        {paymentError && (
                                                                            <p className="mt-2 text-center text-[11px] font-medium text-red-500">
                                                                                {paymentError}
                                                                            </p>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <button
                                                                        onClick={() =>
                                                                            handleStartCurrentStep(
                                                                                stepKey
                                                                            )
                                                                        }
                                                                        className="mt-3 flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 py-2.5 text-[13px] font-semibold text-white shadow-md transition-transform active:scale-[0.98]"
                                                                    >

                                                                        {ui.cta}

                                                                        <ArrowRight className="h-4 w-4" />
                                                                    </button>
                                                                )}

                                                            </div>
                                                        ) : (
                                                            <div
                                                                className={`flex-1 flex items-center gap-2.5 rounded-xl border bg-white px-2.5 py-2 ${isCompleted
                                                                    ? "border-green-200"
                                                                    : "border-gray-200"
                                                                    }`}
                                                            >

                                                                <h3
                                                                    className={`flex-1 min-w-0 truncate text-[13px] font-semibold ${isLocked
                                                                        ? "text-gray-600"
                                                                        : "text-gray-800"
                                                                        }`}
                                                                >
                                                                    {
                                                                        ui.title
                                                                    }
                                                                </h3>

                                                                {isLocked && (
                                                                    <Lock className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                                                )}

                                                            </div>
                                                        )}

                                                    </div>
                                                );
                                            }
                                        )}

                                    </div>

                                </div>

                            </div>
                        </>
                    ) : (
                        <>
                            {/* =================================================
                                SUCCESS
                            ================================================= */}
                            <div className="relative -mt-7 flex flex-col items-center text-center shrink-0">

                                <div className="h-48 w-48">
                                    <Lottie
                                        lottieRef={successLottieRef}
                                        animationData={successAnimation}
                                        loop={false}
                                        autoplay
                                        onComplete={() => {
                                            successLottieRef.current?.goToAndStop(90, true); // pick the frame where confetti is still visible
                                        }}
                                    />
                                </div>

                                <p className="mt-1 text-[25px] font-extrabold text-gray-900 leading-tight">
                                    Congratulations{" "}
                                    {customerName}!
                                </p>

                                <p className="mt-1 text-[14px] font-medium text-green-600">
                                    Your transaction has been successfully completed
                                </p>

                            </div>

                            {/* =================================================
                                SUCCESS BODY
                            ================================================= */}

                            <div className="relative mt-3 flex-1 min-h-0 flex flex-col gap-2 overflow-hidden">

                                {/* Order ID */}

                                <div className="flex items-center justify-center">

                                    <span className="whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-medium text-slate-500 shadow-sm">

                                        Order ID:{" "}

                                        <span className="font-semibold text-slate-800">
                                            {journey?.orderId}
                                        </span>

                                    </span>

                                </div>

                                {/* Loan Summary */}

                                <div className="mt-4 rounded-xl border border-slate-100 bg-white p-2.5 shadow-sm">

                                    <p className="mb-1.5 text-[12px] font-bold leading-tight text-gray-900">
                                        Loan Summary
                                    </p>

                                    <div className="grid grid-cols-[1fr_2fr] border-y border-slate-100">

                                        {/* Loan Amount */}
                                        <div className="flex min-h-[58px] flex-col justify-center px-3 py-2">
                                            <p className="text-[9px] font-medium text-gray-400">
                                                Loan Amount
                                            </p>

                                            <p className="mt-1 text-[12px] font-semibold text-gray-900">
                                                {journey.orderAmount}
                                            </p>
                                        </div>

                                        {/* Product Info */}
                                        <div className="flex min-h-[58px] flex-col justify-center border-l border-slate-100 px-3 py-2">
                                            <p className="text-[9px] font-medium text-gray-400">
                                                Product Info
                                            </p>

                                            <p className="mt-1 line-clamp-2 text-[11px] font-semibold leading-4 text-gray-900">
                                                {journey.productDetails}
                                            </p>
                                        </div>

                                    </div>

                                </div>

                                {/* EMI REMINDER */}

                                <div className="mt-1 relative overflow-hidden flex items-start gap-2.5 rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 px-3.5 py-3">

                                    <div className="absolute -right-4 -top-4 h-16 w-16 rounded-full bg-amber-200/20 blur-2xl" />


                                    <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-amber-600 shadow-sm ring-1 ring-amber-100">

                                        <CalendarClock className="h-4 w-4" />

                                    </div>


                                    <div className="relative">

                                        <p className="text-[11.5px] font-bold text-amber-900 leading-tight">

                                            Keep your wallet funded!

                                        </p>


                                        <p className="mt-0.5 text-[10px] leading-snug text-amber-700">

                                            EMI of{" "}

                                            <span className="font-bold text-amber-800">

                                                {journey.emiPerMonth}

                                            </span>{" "}

                                            will be auto-debited monthly

                                        </p>


                                        {firstEmiDueDate && (

                                            <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 ring-1 ring-amber-200/70">

                                                <span className="text-[9px] font-medium text-amber-700">

                                                    1st EMI due

                                                </span>

                                                <span className="text-[9px] font-bold text-amber-900">

                                                    {firstEmiDueDate}

                                                </span>

                                            </div>

                                        )}

                                    </div>

                                </div>


                                {/* Redirect */}

                                <div className="mt-auto p-2.5">

                                    <div className="flex items-center justify-between">

                                        <p className="text-[10.5px] font-medium text-indigo-700">
                                            Redirecting in
                                        </p>

                                        <span className="text-[12px] font-bold text-indigo-700 tabular-nums">
                                            {secondsLeft}s
                                        </span>

                                    </div>

                                    <div className="mt-1.5 h-1 w-full rounded-full bg-indigo-100 overflow-hidden">

                                        <div
                                            className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-blue-400"
                                            style={{
                                                width: `${redirectProgress}%`,
                                                transition:
                                                    "width 1s linear",
                                            }}
                                        />

                                    </div>

                                </div>

                            </div>
                        </>
                    )}

                    {/* Footer */}

                    <p className="mb-1 mt-2 text-center text-[11px] font-semibold tracking-wide text-gray-400 shrink-0">
                        CleverPe
                    </p>

                </div>
            </div>
        </MobileFrame>
    );
};

export default Dashboard;